import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Table from '../../components/ui/Table';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { listWorkflowCategories } from '../../api/workflow';
import { listExcelTemplates, createExcelTemplate, updateExcelTemplate, deleteExcelTemplate } from '../../api/excelTemplates';
import { pushToast } from '../../components/ui/toastBus';

export const EXCEL_TEMPLATES_QUERY_KEY = ['excel-templates'];

// selection state: { [workflowStepId]: string[] fieldIds }
function TemplateFormModal({ open, onClose, template }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [selected, setSelected] = useState({});
  const [categoryId, setCategoryId] = useState('');
  const [error, setError] = useState('');

  const categoriesQuery = useQuery({
    queryKey: ['workflow-categories'],
    queryFn: listWorkflowCategories,
    enabled: open,
  });
  const categories = useMemo(() => categoriesQuery.data || [], [categoriesQuery.data]);

  useEffect(() => {
    setName(template?.name || '');
    setSelected(Object.fromEntries((template?.selections || []).map((s) => [s.workflow_step_id, s.field_ids])));
    setError('');
  }, [template]);

  useEffect(() => {
    if (!categoryId && categories.length) setCategoryId(String(categories[0].id));
  }, [categories, categoryId]);

  const category = categories.find((c) => String(c.id) === categoryId);
  const steps = (category?.steps || []).filter((s) => (s.fields || []).length > 0);
  const groupName = (id) => category?.groups?.find((g) => g.id === id)?.name;

  const countFor = (c) => (c.steps || []).reduce((n, s) => n + (selected[s.id]?.length || 0), 0);
  const total = Object.values(selected).reduce((n, f) => n + f.length, 0);

  const setStepFields = (stepId, ids) =>
    setSelected((prev) => {
      const next = { ...prev };
      if (ids.length) next[stepId] = ids;
      else delete next[stepId];
      return next;
    });

  const toggleField = (step, fieldId) => {
    const cur = selected[step.id] || [];
    setStepFields(step.id, cur.includes(fieldId) ? cur.filter((f) => f !== fieldId) : [...cur, fieldId]);
  };

  const mutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: name.trim(),
        selections: Object.entries(selected).map(([workflow_step_id, field_ids]) => ({
          workflow_step_id: Number(workflow_step_id),
          field_ids,
        })),
      };
      return template ? updateExcelTemplate(template.id, payload) : createExcelTemplate(payload);
    },
    onSuccess: () => {
      pushToast({
        type: 'success',
        message: template ? 'Template updated.' : 'Template created.',
      });
      queryClient.invalidateQueries({ queryKey: EXCEL_TEMPLATES_QUERY_KEY });
      onClose();
    },
    onError: (err) => setError(err.response?.data?.errors?.name?.[0] || err.response?.data?.message || 'Could not save template.'),
  });

  const handleSave = () => {
    if (!name.trim()) return setError('Name is required.');
    if (total === 0) return setError('Select at least one field.');
    setError('');
    mutation.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={template ? 'Edit Excel template' : 'New Excel template'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={mutation.isPending}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input label="Template name" value={name} onChange={(e) => setName(e.target.value)} />
        <p className="text-xs text-gray-500">
          Pick the fields for each workflow. When a request is downloaded, each candidate gets the fields of their own workflow (one sheet
          per workflow). {total} field(s) selected.
        </p>
        <Select label="Workflow" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {countFor(c) ? ` — ${countFor(c)} selected` : ''}
            </option>
          ))}
        </Select>

        <div className="space-y-3">
          {steps.length === 0 && <p className="text-sm text-gray-500">This workflow has no steps with fields.</p>}
          {steps.map((step) => {
            const ids = step.fields.map((f) => f.id);
            const chosen = selected[step.id] || [];
            return (
              <div key={step.id} className="rounded-md border border-gray-200 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-sm font-medium text-gray-900">
                    {step.title}
                    {groupName(step.step_group_id) && (
                      <span className="ml-2 text-xs font-normal text-gray-400">{groupName(step.step_group_id)}</span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="text-xs text-primary hover:underline"
                    onClick={() => setStepFields(step.id, chosen.length === ids.length ? [] : ids)}
                  >
                    {chosen.length === ids.length ? 'Clear all' : 'Select all'}
                  </button>
                </div>
                <div className="grid gap-x-4 gap-y-1 sm:grid-cols-2">
                  {step.fields.map((f) => (
                    <label key={f.id} className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={chosen.includes(f.id)} onChange={() => toggleField(step, f.id)} />
                      {f.label}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}

export default function ExcelTemplates() {
  const queryClient = useQueryClient();
  const [formTarget, setFormTarget] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const query = useQuery({
    queryKey: EXCEL_TEMPLATES_QUERY_KEY,
    queryFn: listExcelTemplates,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteExcelTemplate,
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Template deleted.' });
      queryClient.invalidateQueries({ queryKey: EXCEL_TEMPLATES_QUERY_KEY });
      setDeleteTarget(null);
    },
    onError: (err) => {
      pushToast({
        type: 'error',
        message: err.response?.data?.message || 'Could not delete template.',
      });
      setDeleteTarget(null);
    },
  });

  const columns = [
    { key: 'name', label: 'Template' },
    { key: 'steps', label: 'Steps', render: (row) => row.selections.length },
    { key: 'field_count', label: 'Fields' },
    {
      key: 'updated_at',
      label: 'Updated',
      render: (row) => new Date(row.updated_at).toLocaleDateString(),
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="flex items-center gap-3 text-sm">
          <button
            onClick={() => {
              setFormTarget(row);
              setFormOpen(true);
            }}
            className="text-primary hover:underline"
          >
            Edit
          </button>
          <button onClick={() => setDeleteTarget(row)} className="text-red-600 hover:underline">
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-gray-500">Column layouts used when downloading a request's candidates as Excel.</p>
        <Button
          onClick={() => {
            setFormTarget(null);
            setFormOpen(true);
          }}
        >
          New Template
        </Button>
      </div>

      <Table columns={columns} data={query.data || []} isLoading={query.isLoading} emptyMessage="No Excel templates yet." />

      {formOpen && <TemplateFormModal open={formOpen} onClose={() => setFormOpen(false)} template={formTarget} />}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
        title="Delete Excel template"
        message={`Delete "${deleteTarget?.name}"?`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
